// client/src/coms/e2ee/dmStatus.js
// What a DM header says about encryption. The server reports both participants' preferences and whether the
// thread is in encrypted mode; the lock is only shown when this device has itself established the encrypted
// session (cryptoSession.js threadSession). A server that claims 'e2ee' while this device has not set the session
// up is shown as "setting up" or as a warning, never as a lock, so a database flag alone never produces a privacy
// indicator.

export const ACTIVE_TEXT = 'End-to-end encryption is active. Only participants’ authorised devices can read new messages.';
export const OTHER_DISABLED_TEXT = 'This conversation is not end-to-end encrypted because the other participant has encryption disabled.';
export const UNVERIFIED_NOTE = 'You have not verified this contact yet: compare safety codes to make sure only their devices hold the keys.';
export const VERIFIED_NOTE = 'You verified this contact’s identity; only devices they signed receive the keys.';

const STANDARD = { tone: 'standard', icon: '🔓', label: 'Standard', text: 'Standard conversation: messages are not end-to-end encrypted.' };

// local: { state: 'none' | 'pending' | 'ready' | 'failed' | 'unsupported', error, code }
// contact: { verified } once this device has looked up the other participant's identity
// tone: 'secure' | 'warning' | 'standard'
export function describeDmEncryption(e2ee, { otherName = '', local = null, contact = null } = {}) {
  const s = e2ee && typeof e2ee === 'object' ? e2ee : null;
  const who = String(otherName || '').trim() || 'The other participant';
  if (!s) return STANDARD;
  if (s.status === 'e2ee') {
    const st = (local && local.state) || 'none';
    if (st === 'ready') {
      const verified = !!(contact && contact.verified);
      return {
        tone: 'secure',
        icon: '🔒',
        label: verified ? 'End-to-end encrypted · verified' : 'End-to-end encrypted',
        text: `${ACTIVE_TEXT} ${verified ? VERIFIED_NOTE : UNVERIFIED_NOTE}`,
        action: verified ? '' : 'verify',
      };
    }
    if (st === 'unsupported') {
      return {
        tone: 'warning',
        icon: '⚠️',
        label: 'Encryption unavailable here',
        text: 'This conversation is end-to-end encrypted, but this browser cannot run the encryption, so you cannot read or send messages in it here.',
      };
    }
    if (st === 'failed' && local.code === 'identity_changed') {
      return {
        tone: 'warning',
        icon: '⚠️',
        label: 'Identity changed',
        text: `${who}’s encryption identity changed. That happens when they reset encryption, but it could also mean someone is trying to intercept this conversation. Nothing is sent to them until you review the change (ideally confirm it with them first).`,
        action: 'accept_identity',
      };
    }
    if (st === 'failed' && (local.code === 'device_unverified' || local.code === 'identity_missing' || local.code === 'device_removed')) {
      return {
        tone: 'warning',
        icon: '⚠️',
        label: 'This device is not verified',
        text: 'This browser is not yet verified with your encryption identity, so it cannot send or read encrypted messages here. Verify it with your recovery key.',
        action: 'verify_device',
      };
    }
    if (st === 'failed' && local.code === 'identity_unknown') {
      return {
        tone: 'warning',
        icon: '⏳',
        label: 'Waiting for the other participant',
        text: `${who} has not finished setting up encryption on a verified device yet. Sending is paused: nothing will be sent unencrypted.`,
        retry: true,
      };
    }
    if (st === 'failed') {
      return {
        tone: 'warning',
        icon: '⚠️',
        label: 'Encryption setup failed',
        text: `End-to-end encryption could not be set up on this device${local.error ? ` (${local.error})` : ''}. Sending is paused: nothing will be sent unencrypted.`,
        retry: true,
      };
    }
    return {
      tone: 'standard',
      icon: '⏳',
      label: 'Setting up encryption',
      text: 'Both of you have encryption on. This device is setting up the encrypted session; sending is paused until it is ready.',
    };
  }
  switch (s.reason) {
    case 'other_disabled':
      return { tone: 'warning', icon: '⚠️', label: 'Not encrypted', text: OTHER_DISABLED_TEXT };
    case 'self_disabled':
      return {
        ...STANDARD,
        text: `Standard conversation: not end-to-end encrypted. ${who} has encryption turned on; turn yours on in Settings → Privacy & Security to make this conversation eligible.`,
      };
    case 'session_pending':
      return {
        ...STANDARD,
        text: 'Both of you have encryption turned on, but one of you has not set up an encryption device yet, so this conversation is still standard (not end-to-end encrypted).',
      };
    default:
      return STANDARD;
  }
}
